/**
 * PalletScanMode — floor-friendly, single-screen scan loop for posting
 * pallet movements at speed.
 *
 * UX
 * ---
 * Pickers/packers configure direction (IN/OUT), pallet type, qty-per-scan
 * and optional merchant/warehouse at the top of the screen. The big "Open
 * scanner" button launches the existing `BarcodeScanner` (camera + manual
 * entry). Every successful scan posts a movement of the configured size
 * and pushes a row onto the session log; the counter updates instantly.
 *
 * Smart pallet-type switching
 * ---------------------------
 * If the scanned barcode is exactly a known pallet TYPE code (e.g. "WOOD",
 * "CHEP", "LOSCAM") we treat it as a type-selector instead of a movement.
 * This lets warehouses print laminated cards near each stack so the
 * operator can swap type with the same scan reflex they use for SKUs.
 *
 * The route lives at `/pallets/scan` and is granted to
 * admin/manager/picker/packer roles in App.js.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Stack, Barcode, ArrowDown, ArrowUp, ArrowCounterClockwise, Check, X,
  SpeakerHigh, SpeakerSlash, SpeakerLow, SpeakerSimpleHigh,
} from '@phosphor-icons/react';
import BarcodeScanner from '../components/BarcodeScanner';
import useScanAnywhere from '../hooks/useScanAnywhere';
import { playSuccessTone, playErrorTone, readSoundLevel } from '../lib/audioFeedback';

// Four levels in display order. Cycling through these via the segmented
// pill lets a glove-wearing operator nudge the volume without leaving the
// scan screen; a busy DC can stamp it to LOUD, a quiet aisle to QUIET.
const SOUND_LEVELS = [
  { value: 'off',    label: 'Off',    Icon: SpeakerSlash,      tone: 'border-slate-300 bg-slate-100 text-slate-600' },
  { value: 'quiet',  label: 'Quiet',  Icon: SpeakerLow,        tone: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  { value: 'normal', label: 'Normal', Icon: SpeakerHigh,       tone: 'border-emerald-300 bg-emerald-100 text-emerald-800' },
  { value: 'loud',   label: 'Loud',   Icon: SpeakerSimpleHigh, tone: 'border-emerald-400 bg-emerald-200 text-emerald-900' },
];
import {
  listPalletTypes, getWarehouses, list3plMerchants,
  createPalletMovement, voidPalletMovement, seedPalletDefaults,
} from '../lib/api';

const DIRECTIONS = [
  { value: 'in', label: 'IN', icon: ArrowDown, tone: 'bg-emerald-600 hover:bg-emerald-700' },
  { value: 'out', label: 'OUT', icon: ArrowUp, tone: 'bg-rose-600 hover:bg-rose-700' },
];

export default function PalletScanMode() {
  const [types, setTypes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scanner config
  const [direction, setDirection] = useState('in');
  const [typeId, setTypeId] = useState('');
  const [qtyPerScan, setQtyPerScan] = useState(1);
  const [warehouseId, setWarehouseId] = useState('');
  const [merchantId, setMerchantId] = useState('');

  // Scan session
  const [scannerOpen, setScannerOpen] = useState(false);
  const [session, setSession] = useState([]);  // [{id, ts, kind, type_id, type_code, qty, status: 'ok'|'err', barcode}]
  const [busy, setBusy] = useState(false);

  // Sound preference — 4-state ('off' | 'quiet' | 'normal' | 'loud')
  // persisted to localStorage so the operator's choice sticks across
  // sessions. Default 'normal' because audio rhythm is the most common
  // ask; warehouses can dial up to LOUD on noisy floors or down to OFF
  // on shared/quiet stations from the segmented pill in the header.
  const [soundLevel, setSoundLevel] = useState(() => readSoundLevel());
  const setLevel = (next) => {
    setSoundLevel(next);
    try {
      window.localStorage.setItem('palletScanSound', next);
    } catch { /* private mode etc — best effort */ }
    // Audible confirmation at the new level (skipped for 'off' since the
    // tone helper itself no-ops at that level — the visual flip is the
    // confirmation in that case).
    if (next !== 'off') playSuccessTone(next);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await seedPalletDefaults().catch(() => null);
        const [t, w, m] = await Promise.all([
          listPalletTypes(),
          getWarehouses().catch(() => ({ data: [] })),
          list3plMerchants().catch(() => ({ data: [] })),
        ]);
        const active = (t.data || []).filter((x) => x.is_active);
        setTypes(active);
        setWarehouses(w.data || []);
        setMerchants(m.data || []);
        const def = active.find((x) => x.code === 'WOOD') || active[0];
        if (def) setTypeId(def.id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load scan-mode data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentType = types.find((t) => t.id === typeId);

  const tally = useMemo(() => {
    const map = {};
    session.filter((r) => r.status === 'ok').forEach((r) => {
      const k = `${r.kind}:${r.type_code}`;
      map[k] = (map[k] || 0) + r.qty;
    });
    return map;
  }, [session]);

  const totalMoved = useMemo(
    () => session.filter((r) => r.status === 'ok').reduce((s, r) => s + r.qty, 0),
    [session],
  );

  const postMovement = async (overrideType = null, barcode = null) => {
    const t = overrideType || currentType;
    if (!t) {
      toast.error('Pick a pallet type first');
      return;
    }
    const qty = Number(qtyPerScan);
    if (!qty || qty <= 0) {
      toast.error('Qty per scan must be > 0');
      return;
    }
    setBusy(true);
    const tempId = `tmp-${Date.now()}`;
    try {
      const payload = {
        type_id: t.id,
        kind: direction,
        qty,
      };
      if (warehouseId) payload.warehouse_id = warehouseId;
      if (merchantId) payload.merchant_id = merchantId;
      if (barcode) payload.reference = `Scan: ${barcode}`;
      const r = await createPalletMovement(payload);
      setSession((prev) => [
        {
          id: r.data?.id || tempId,
          ts: new Date().toISOString(),
          kind: direction,
          type_id: t.id,
          type_code: t.code,
          qty,
          status: 'ok',
          barcode: barcode || '—',
        },
        ...prev,
      ]);
      // Lightweight haptic via the Web Vibration API where available — quick
      // confirmation feedback for floor users wearing gloves who can't see
      // the toast immediately. Audio "ding" runs alongside so the rest of
      // the team learns scan rhythm without watching the screen.
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(40);
      if (soundLevel !== 'off') playSuccessTone(soundLevel);
    } catch (e) {
      setSession((prev) => [
        {
          id: tempId, ts: new Date().toISOString(),
          kind: direction, type_id: t.id, type_code: t.code, qty,
          status: 'err', barcode: barcode || '—',
          error: e?.response?.data?.detail || 'Save failed',
        },
        ...prev,
      ]);
      // Long buzz pattern + low audio buzz so an error is unmistakable
      // even when the operator is looking elsewhere on the floor.
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate([80, 60, 80]);
      if (soundLevel !== 'off') playErrorTone(soundLevel);
      toast.error(e?.response?.data?.detail || 'Movement failed');
    } finally {
      setBusy(false);
    }
  };

  const handleScan = (decoded) => {
    setScannerOpen(false);
    if (!decoded) return;
    handleDecodedCode(decoded, /* reopenScanner */ true);
  };

  // Pure scan resolver — used by both the camera (which re-opens the
  // scanner overlay after each scan) and the global RF scan-anywhere
  // listener (which doesn't have a camera overlay to re-open).
  const handleDecodedCode = (decoded, reopenScanner) => {
    if (!decoded) return;
    const code = String(decoded).trim().toUpperCase();
    // Type-switch: scan a card labelled with a known type code → swap type
    const matchedType = types.find((t) => t.code === code);
    if (matchedType) {
      setTypeId(matchedType.id);
      toast.success(`Type switched to ${matchedType.name}`);
      if (reopenScanner) setTimeout(() => setScannerOpen(true), 200);
      return;
    }
    // Otherwise treat the scan as a pallet barcode → post a movement.
    postMovement(null, decoded);
    if (reopenScanner) setTimeout(() => setScannerOpen(true), 200);
  };

  // RF scan-anywhere — operators on the floor can pull their RF gun
  // anywhere on the page and have it post a movement without ever
  // touching the screen. Disabled while the camera overlay is open
  // (it owns the input surface) and while we're busy posting (so a
  // double-pull doesn't queue duplicate movements). The hook itself
  // ignores keystrokes when an editable element has focus, so the
  // qty/type/warehouse inputs continue to work normally.
  const scanAnywhereActive = !scannerOpen && !busy && !!currentType;
  useScanAnywhere(scanAnywhereActive, (code) => handleDecodedCode(code, false));

  const undoLast = async () => {
    const last = session.find((r) => r.status === 'ok');
    if (!last) {
      toast.info('Nothing to undo');
      return;
    }
    if (!window.confirm(`Undo last ${last.kind.toUpperCase()} of ${last.qty} × ${last.type_code}?`)) return;
    try {
      await voidPalletMovement(last.id);
      setSession((prev) => prev.map((r) =>
        r.id === last.id ? { ...r, status: 'voided' } : r));
      toast.success('Last scan voided');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Void failed');
    }
  };

  const clearSession = () => {
    if (!session.length) return;
    if (!window.confirm('Clear local session log? Movements stay in the database — this only hides them on this screen.')) return;
    setSession([]);
  };

  if (loading) {
    return <Layout><div className="p-12 text-center text-muted-foreground">Loading scan mode…</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4" data-testid="pallet-scan-mode-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Barcode size={28} /> Pallet Scan Mode
            </h1>
            <p className="text-sm text-muted-foreground">
              Configure once at the top, then scan to post movements at floor speed.
              Scan a pallet-type code (WOOD/CHEP/LOSCAM) to swap type without leaving the scanner.
            </p>
          </div>
          {/* Sound level — segmented pill cycling Off · Quiet · Normal · Loud.
              Each segment is a single-tap target so glove-wearing operators
              don't have to drill into a settings menu; the selected segment
              fills with a green ramp that visually matches the loudness. */}
          <div
            role="radiogroup"
            aria-label="Scan sound level"
            data-testid="scan-sound-toggle"
            className="shrink-0 inline-flex items-stretch border border-border divide-x divide-border bg-card overflow-hidden"
          >
            {SOUND_LEVELS.map((opt) => {
              const selected = soundLevel === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setLevel(opt.value)}
                  data-testid={`scan-sound-${opt.value}`}
                  title={`Sound: ${opt.label}`}
                  className={`inline-flex items-center gap-1 px-2.5 h-9 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                    selected
                      ? `${opt.tone} border-0`
                      : 'bg-transparent text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <opt.Icon size={14} weight={selected ? 'bold' : 'regular'} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Direction big toggle */}
        <div className="grid grid-cols-2 gap-2">
          {DIRECTIONS.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => setDirection(d.value)}
              data-testid={`scan-direction-${d.value}`}
              className={`h-16 text-white font-bold uppercase tracking-widest text-lg flex items-center justify-center gap-2 ${
                direction === d.value ? d.tone : 'bg-slate-300 text-slate-700 hover:bg-slate-400'
              }`}
            >
              <d.icon size={28} weight="bold" /> {d.label}
            </button>
          ))}
        </div>

        {/* Config strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-card border border-border p-3">
          <div>
            <Label className="text-xs">Pallet type</Label>
            <select
              value={typeId} onChange={(e) => setTypeId(e.target.value)}
              className="w-full border border-border bg-background h-10 px-2 text-sm"
              data-testid="scan-type-select"
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} · {t.name}{t.is_pooled ? ' (pooled)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Qty per scan</Label>
            <Input type="number" min="1" value={qtyPerScan}
              onChange={(e) => setQtyPerScan(e.target.value)}
              className="h-10" data-testid="scan-qty-input" />
          </div>
          <div>
            <Label className="text-xs">Warehouse (optional)</Label>
            <select
              value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
              className="w-full border border-border bg-background h-10 px-2 text-sm"
              data-testid="scan-warehouse-select"
            >
              <option value="">—</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Merchant (optional)</Label>
            <select
              value={merchantId} onChange={(e) => setMerchantId(e.target.value)}
              className="w-full border border-border bg-background h-10 px-2 text-sm"
              data-testid="scan-merchant-select"
            >
              <option value="">—</option>
              {merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>

        {/* Big scan button */}
        <Button
          onClick={() => setScannerOpen(true)}
          disabled={busy || !currentType}
          className="w-full h-20 text-xl font-bold uppercase tracking-widest bg-amber-600 hover:bg-amber-700"
          data-testid="scan-open-btn"
        >
          <Barcode size={28} weight="bold" className="mr-2" />
          {busy ? 'Posting…' : `Scan to post ${qtyPerScan} × ${currentType?.code || '—'}`}
        </Button>

        {scanAnywhereActive && (
          <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-widest font-bold text-emerald-700 -mt-2"
            data-testid="scan-anywhere-indicator"
            title="Pull your RF gun anywhere on this page — keystrokes route here automatically.">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            RF scan-anywhere armed · pull the gun without touching the screen
          </div>
        )}

        {/* Tally */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-emerald-200 bg-emerald-50 p-3" data-testid="scan-tally-in">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Posted IN this session</div>
            <div className="text-3xl font-bold font-mono text-emerald-900">
              {Object.entries(tally).filter(([k]) => k.startsWith('in:'))
                .reduce((s, [, v]) => s + v, 0)}
            </div>
            <div className="text-[10px] font-mono text-emerald-700 truncate">
              {Object.entries(tally).filter(([k]) => k.startsWith('in:'))
                .map(([k, v]) => `${k.split(':')[1]}: ${v}`).join(' · ') || '—'}
            </div>
          </div>
          <div className="border border-rose-200 bg-rose-50 p-3" data-testid="scan-tally-out">
            <div className="text-[10px] uppercase tracking-widest text-rose-700">Posted OUT this session</div>
            <div className="text-3xl font-bold font-mono text-rose-900">
              {Object.entries(tally).filter(([k]) => k.startsWith('out:'))
                .reduce((s, [, v]) => s + v, 0)}
            </div>
            <div className="text-[10px] font-mono text-rose-700 truncate">
              {Object.entries(tally).filter(([k]) => k.startsWith('out:'))
                .map(([k, v]) => `${k.split(':')[1]}: ${v}`).join(' · ') || '—'}
            </div>
          </div>
        </div>

        {/* Action strip */}
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Total movements posted: <strong className="font-mono">{totalMoved}</strong>
          </div>
          <div className="flex gap-1.5">
            <Button onClick={undoLast} variant="outline" size="sm" data-testid="scan-undo-btn">
              <ArrowCounterClockwise size={14} className="mr-1" /> Undo last
            </Button>
            <Button onClick={clearSession} variant="outline" size="sm" data-testid="scan-clear-btn">
              <X size={14} className="mr-1" /> Clear log
            </Button>
          </div>
        </div>

        {/* Session log */}
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left">
                <th className="p-2">When</th>
                <th className="p-2">Direction</th>
                <th className="p-2">Type</th>
                <th className="p-2 text-right">Qty</th>
                <th className="p-2">Barcode</th>
                <th className="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {session.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                  Scan a pallet to start the session log.
                </td></tr>
              )}
              {session.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-b-0" data-testid={`scan-log-row-${r.id}`}>
                  <td className="p-2 text-xs whitespace-nowrap">{new Date(r.ts).toLocaleTimeString()}</td>
                  <td className="p-2">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${
                      r.kind === 'in' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                      {r.kind}
                    </span>
                  </td>
                  <td className="p-2 font-mono">{r.type_code}</td>
                  <td className="p-2 text-right font-mono">{r.qty}</td>
                  <td className="p-2 text-xs truncate max-w-[160px]">{r.barcode}</td>
                  <td className="p-2">
                    {r.status === 'ok' && (
                      <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 inline-flex items-center gap-1">
                        <Check size={10} weight="bold" /> OK
                      </span>
                    )}
                    {r.status === 'voided' && (
                      <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5">Voided</span>
                    )}
                    {r.status === 'err' && (
                      <span className="text-[10px] uppercase tracking-wider bg-rose-100 text-rose-800 px-1.5 py-0.5" title={r.error}>
                        Error
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {scannerOpen && (
        <BarcodeScanner
          title={`Scan pallet · ${direction.toUpperCase()} · ${currentType?.code}`}
          onScan={handleScan}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </Layout>
  );
}
